import React, { createRef, Fragment } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { RawHTML } from '@wordpress/element';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { Path } from 'path-parser';
import PropTypes from 'prop-types';

import './postListing.scss';

const appElement = document.querySelector('.post-listing');

const DISPLAY_COUNT = 10;
const MOBILE_BREAKPOINT = 768;

class PostListing extends React.Component {
  /**
   * Constructor. Binds function scope.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      activeSearch: undefined,
      activeTopic: {},
      currentsLoading: true,
      displayCount: DISPLAY_COUNT,
      incrementAmount: DISPLAY_COUNT,
      loadedPages: 0,
      postList: [],
      searchString: undefined,
      topicList: [],
      topicsLoading: true,
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.onTopicActivate = this
      .onTopicActivate.bind(this);
    this.onTopicSelect = this
      .onTopicSelect.bind(this);
    this.onClearSearch = this
      .onClearSearch.bind(this);
    this.onViewAll = this
      .onViewAll.bind(this);
    this.handleMoveToApp = this.handleMoveToApp.bind(this);
    this.getTopicObject = this
      .getTopicObject.bind(this);
    this.getSelectedTopic = this
      .getSelectedTopic.bind(this);

    // Create ref.
    this.postListingRef = createRef();
    this.isViewLoaded = false;
  }

  /**
   * Fires on mount.
   */
  componentDidMount() {
    this.mounted = true;

    // Fetch topic list.
    this.fetchtopicList = apiFetch({
      path: '/wp-json/kauffman/v1/topics',
      parse: false,
    })
      .then((response) => response.json())
      .then((data) => {
        if (this.mounted) {
          const topicList = data.filter((item) => 0 !== item.count);
          this.setState({
            topicsLoading: false,
            topicList,
          });

          const topicObject = this.getTopicObject();

          // Only proceed if we return a match.
          if (null !== topicObject) {
            this.setState({
              activeTopic: {
                id: topicObject.id,
                name: topicObject.name,
                slug: topicObject.slug,
              },
              displayCount: topicObject.count,
            });
          }
        }
      }).catch(() => {
        if (this.mounted) {
          this.setState({
            topicsLoading: false,
            topicList: [],
          });
        }
      });

    let totalPages = 0;
    // Fetch posts of type "current".
    this.fetchCurrentPosts = apiFetch({
      path: '/wp-json/wp/v2/current?page=1&per_page=100&orderby=id&order=asc',
      parse: false,
    })
      .then((response) => {
        totalPages = parseInt(response.headers.get('X-WP-TotalPages'), 10);
        return response.json();
      })
      .then((data) => {
        this.setState({
          postList: [
            ...this.state.postList,
            ...data,
          ],
          loadedPages: this.state.loadedPages + 1,
          currentsLoading: totalPages !== this.state.loadedPages + 1,
        });
        // For remaining pages, fetch
        [...Array(totalPages - 1).keys()].forEach((page) => {
          apiFetch({
            path: `/wp-json/wp/v2/current?per_page=100&page=${page + 2}&orderby=id&order=asc`, // eslint-disable-line max-len
          })
            .then((response) => {
              const newPostList = [
                ...this.state.postList,
                ...response,
              ];
              // When the last page has been loaded, sort by date.
              if (this.state.loadedPages + 1 === totalPages) {
                newPostList.sort((itemA, itemB) => new Date(itemB.date) - new Date(itemA.date)); // eslint-disable-line max-len
              }
              this.setState({
                postList: newPostList,
                loadedPages: this.state.loadedPages + 1,
                currentsLoading: totalPages !== this.state.loadedPages + 1,
              });
            });
        });
      }).catch(() => {
        if (this.mounted) {
          this.setState({
            currentsLoading: false,
            postList: [],
          });
        }
      });
  }

  /**
   * Move to app if topic exists.
   * Ref not yet available in ComponentDidMount.
   */
  componentDidUpdate() {
    // Only proceed if we return a match.
    if (null !== this.getTopicObject() && ! this.isViewLoaded) {
      this.handleMoveToApp();
    }
  }

  /**
   * Fires when Component unmounts.
   */
  componentWillUnmount() {
    this.mounted = false;
    this.isViewLoaded = false;
  }

  /**
   * Sets an active topic.
   */
  onTopicActivate(topic) {
    // Update url here.
    if ('all' === topic) {
      this.setState({
        activeTopic: {},
        displayCount: DISPLAY_COUNT, // Reset count.
      });
    } else {
      this.setState({
        activeTopic: {
          count: topic.count,
          id: topic.id,
          name: topic.name,
          slug: topic.slug,
        },
        displayCount: DISPLAY_COUNT, // Reset count.
      });
    }
    // Set url always.
    const activeTopic = 'all' !== topic ? topic : '';
    this.handleUrlUpdate(activeTopic);
  }

  /**
   * Sets an active topic on view-all
   * And sets display count and url.
   *
   * @param {object} topic topic object.
   */
  onViewAll(topic) {
    if (topic && topic.count) {
      this.setState({
        displayCount: topic.count,
      });
      this.handleUrlUpdate(topic);
    }
  }

  /**
   * Sets a topic via dropdown selection.
   */
  onTopicSelect(topicEvent) {
    const {
      topicList,
    } = this.state;
    if ('all' === topicEvent.target.value) {
      this.onTopicActivate('all');
    } else {
      const selectedTopicId = parseInt(topicEvent.target.value, 10);
      const topicObj = topicList.find((topic) => topic.id === selectedTopicId);
      this.onTopicActivate(topicObj);
    }
  }

  /**
   * Resets active search.
   */
  onClearSearch() {
    this.setState({
      activeSearch: undefined,
      searchString: undefined,
      displayCount: DISPLAY_COUNT,
    });
  }

  /**
   * Get Selected topic from URL.
   *
   * @return {string} selectedTopic topic slug.
   */
  getSelectedTopic() {
    const {
      rootName,
    } = this.props;

    // Create path for app.
    const path = new Path(`/${rootName}/topic/:topic`);
    const selectedTopic = path.test(window.location.pathname); // phpcs:ignore WordPressVIPMinimum.JS.Window.location

    return selectedTopic;
  }

  /**
   * Return selected topic from URL param if it exists.
   *
   * @return {object||null} topic object or null.
   */
  getTopicObject() {
    const {
      topicList,
    } = this.state;

    const selectedTopic = this.getSelectedTopic();

    if (! selectedTopic || 0 === topicList.length) {
      return null;
    }

    // Get active topic from url.
    const topicArray = topicList.filter((topic) => selectedTopic.topic === topic.slug); // eslint-disable-line max-len
    const topicObject = topicArray && 0 < topicArray.length ? topicArray[0] : null; // eslint-disable-line max-len

    return topicObject;
  }

  /**
   * Sets search state based on user input.
   * @param {string} source - key or undefined (ex. button click).
   * @param {object} event - JS event object.
   */
  handleSearch(source, event) {
    if (
      (('key' === source && 'Enter' === event.key) || 'button' === source)
    ) {
      if (this.state.searchString && 2 < this.state.searchString.length) {
        this.setState({
          activeSearch: this.state.searchString,
          activeTopic: {},
          displayCount: DISPLAY_COUNT,
        });
        this.handleUrlUpdate();
      }
    }
  }

  /**
   * Handles window url update.
   *
   * @param {object} topic topic selected, optional.
   */
  handleUrlUpdate(topic) {
    const {
      rootName,
    } = this.props;

    if (! topic) {
      window.history.pushState('', '', `/${rootName}/`);
      return;
    }

    const topicUrl = `/${rootName}/topic/${topic.slug}`;
    window.history.pushState({ url: topicUrl }, '', topicUrl);
  }

  /**
   * Determines if a Current matches applied filters.
   * @param {object} item - the Current post object.
   * @returns {boolean}
   */
  filterPosts(item) {
    const {
      activeTopic,
      activeSearch,
    } = this.state;

    // If there's an active search, check if the current's title contains the search string.
    let titleMatch = false;
    if (activeSearch) {
      // Set up regex for text search if applicable
      const escapedInput = activeSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedInput, 'i');
      titleMatch = regex.test(item.title.rendered);
    }

    let itemMatch;
    // If there is an active topic.
    if (activeTopic.id && item.topics) {
      itemMatch = item.topics.find((topicId) => topicId === activeTopic.id);
    }

    if (activeSearch && activeTopic.id) {
      return titleMatch && itemMatch;
    } else if (activeSearch) {
      return titleMatch;
    } else if (activeTopic.id) {
      return itemMatch;
    }
    return true;
  }

  /**
   * Takes the id list of a Current's topics and returns the names.
   * @param {object} post - the Current.
   * @returns {string} - the list of topic names.
   */
  formatTopicNames(post) {
    const allTopics = post.topics.map((id) => {
      const foundTopic = this.state.topicList.find((topic) =>
        id === topic.id);
      return foundTopic ? foundTopic.name : undefined;
    });
    return allTopics.join('  |  ');
  }

  /**
   * Increments the number of displayed currents.
   */
  loadMore() {
    const newDisplayCount =
      this.state.displayCount + this.state.incrementAmount;
    this.setState({
      displayCount: newDisplayCount,
    });
  }

  /**
   * Move window to div if topic exists.
   */
  handleMoveToApp() {
    if (this.postListingRef && null !== this.postListingRef.current) {
      this.isViewLoaded = true;
      this.postListingRef.current.scrollIntoView(true);
    }
  }

  /**
   * Renders component markup.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      activeTopic,
      currentsLoading,
      displayCount,
      incrementAmount,
      postList,
      topicList,
      topicsLoading,
    } = this.state;

    if (! appElement) {
      return false;
    }

    if (
      ! topicList
      || ! postList
      || ! activeTopic
      || 0 === postList.length
    ) {
      return false;
    }

    // Filter posts by selection.
    const filteredPosts = postList.filter((item) => this.filterPosts(item));

    return (
      <div
        className="post-listing__container"
        ref={this.postListingRef}
      >
        <h3 className="block__heading">
          {__('Explore By Topic', 'kauffman')}
        </h3>
        {(currentsLoading || topicsLoading) ? (
          <div className="post-listing__loading">
            {__('Loading...', 'kauffman')}
          </div>
        ) : (
          <Fragment>
            <div className="post-listing__filters">
              <div className="post-listing__input search">
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label htmlFor="searchSelect">
                  {/* eslint-enable jsx-a11y/label-has-for */}
                  {__('Search', 'kauffman')}
                </label>
                <div className="search-form">
                  <input
                    id="searchSelect"
                    className="post-listing__text-input"
                    onChange={(event) =>
                      this.setState({
                        searchString: event.target.value,
                      })}
                    onKeyDown={(event) =>
                      this.handleSearch('key', event)}
                    placeholder={
                      __('Search Currents', 'kauffman')}
                    type="text"
                    value={DOMPurify.sanitize(this.state.searchString)}
                  />
                  <button
                    className="search-form__button"
                    onClick={() => this.handleSearch('button')}
                  >
                    {__('Search', 'kauffman')}
                  </button>
                </div>
                <button
                  className="searchReset"
                  onClick={() => this.onClearSearch()}
                >
                  {__('Reset', 'kauffman')}
                </button>
              </div>
            </div>
            <div className="post-listing__panel">
              {0 < topicList.length && (
                /* Render a list for desktop */
                (MOBILE_BREAKPOINT < window.innerWidth) ? (
                  <div className="post-listing__toolbar">
                    <ul className="post-listing__toolbar-inner">
                      <li>
                        <button
                          className={classNames([
                            'post-listing__toolbar-item',
                            { 'is-active': ! activeTopic.id },
                          ])}
                          onClick={() => this.onTopicActivate('all')}
                        >
                          <div className="toolbar-item__title">All topics</div>
                        </button>
                      </li>
                      {topicList.map((topic) => {
                        const activeCl = topic.id === activeTopic.id
                          ? 'is-active' : '';
                        return (
                          <li key={topic.id}>
                            <button
                              className={
                                `post-listing__toolbar-item ${activeCl}`
                              }
                              onClick={() => this.onTopicActivate(topic)}
                            >
                              <div className="toolbar-item__title">
                                <RawHTML>
                                  {DOMPurify.sanitize(topic.name)}
                                </RawHTML>
                              </div>
                              {topic.description && (
                                <div className="toolbar-item__description">
                                  <RawHTML>
                                    {DOMPurify.sanitize(topic.description)}
                                  </RawHTML>
                                </div>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  /* Render a dropdown for mobile */
                  <div className="post-listing__toolbar">
                    <label htmlFor="topic-select">
                      <div className="post-listing__toolbar-select-label">
                        {__('Filter by topic', 'kauffman')}
                      </div>
                      <select
                        className="post-listing__toolbar-select"
                        name="topic-select"
                        id="topic-select"
                        onChange={(event) => this.onTopicSelect(event)}
                      >
                        <option value="all">
                          {__('All topics', 'kauffman')}
                        </option>
                        {topicList.map((topic) => (
                          <option
                            key={topic.id}
                            value={topic.id}
                          >
                            {__(topic.name, 'kauffman')}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )
              )}
              <div className="post-listing__panel-content">
                {0 === filteredPosts.length && (
                  <div className="post-listing__loading">
                    {__('No Currents match the applied filters.', 'kauffman')}
                  </div>
                )}
                {0 < filteredPosts.length && (
                  <Fragment>
                    <div className="post-listing__postCount">
                      {`${filteredPosts.length} result${(1 === filteredPosts.length) ? '' : 's'} found`} {/* eslint-disable-line max-len */ }
                      {
                        0 < Object.keys(activeTopic).length
                        && (0 < filteredPosts.length)
                        && (
                          displayCount < filteredPosts.length
                          && Math.abs(filteredPosts.length - displayCount) > incrementAmount // eslint-disable-line max-len
                        )
                        && (
                          <button
                            onClick={() => this.onViewAll(activeTopic)}
                          >
                            {__('(View All)', 'kauffman')}
                          </button>
                        )
                      }
                    </div>
                    <ul className="post-listing__posts">
                      {filteredPosts.slice(0, displayCount).map((post) => {
                        const displayVideoOverlay =
                          post.meta.kauffman_is_video_current ||
                          post.meta.kauffman_media_url;
                        const videoLength =
                          post.meta.kauffman_video_length;
                        const titleTrimmed = post.title
                          ? DOMPurify.sanitize(post.title.rendered) : '';
                        const postDate = post.date
                          ? moment(post.date).format('MMMM D, YYYY') : '';
                        /* eslint-disable no-underscore-dangle, max-len */
                        const imageFileName = post.kauffman_thumbnail_url;
                        // Remove pre-existing resize strings if they exist.
                        const featuredImage = - 1 < imageFileName.lastIndexOf('?resize') ? imageFileName.substring(0, imageFileName.indexOf('?resize')) : imageFileName;
                        /* eslint-enable */
                        return (
                          <li
                            key={post.id}
                            className="post-listing__post"
                          >
                            <a // eslint-disable-line jsx-a11y/anchor-has-content
                              className="card__cover"
                              href={post.link}
                            />
                            {featuredImage ? (
                              <div className="card__image">
                                <img
                                  alt={titleTrimmed}
                                  src={`${featuredImage}?resize=250x250`} // eslint-disable-line max-len
                                />
                                {
                                  displayVideoOverlay && (
                                    <Fragment>
                                      <div className="overlay" />
                                      <span className="video-length">
                                        {videoLength}
                                      </span>
                                      <span className="icon-play-video" />
                                    </Fragment>
                                  )
                                }
                              </div>
                            ) : <div className="card__image" /> }
                            <div className="card__header">
                              <div className="card__topic">
                                {this.formatTopicNames(post)}
                              </div>
                              <div className="card__title">
                                <a href={post.link}>
                                  <RawHTML>
                                    {titleTrimmed}
                                  </RawHTML>
                                </a>
                              </div>
                              {postDate && (
                                <div className="card__pubdate">
                                  {postDate}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </Fragment>
                )}
              </div>
            </div>
            {(0 < filteredPosts.length)
              && (displayCount < filteredPosts.length && Math.abs(filteredPosts.length - displayCount) > incrementAmount) && // eslint-disable-line max-len
              (
                <div className="post-listing__button-container">
                  <button
                    className="wp-block-button"
                    onClick={this.loadMore}
                  >
                    <div className="wp-block-button__link">
                      {__('Load More', 'kauffman')}
                    </div>
                  </button>
                </div>
              )
            }
          </Fragment>
        )}
      </div>
    );
  }
}

/**
 * Set PropTypes for this component.
 * @type {object}
 */
PostListing.propTypes = {
  rootName: PropTypes.string.isRequired,
};

if (appElement) {
  // Get pathname root dynamically for saftey. Should always be 'currents'.
  const pathNames = window.location.pathname // phpcs:ignore WordPressVIPMinimum.JS.Window.VarAssignment
    .split('/').filter((name) => '' !== name);
  const rootName = pathNames && 0 < pathNames.length ? pathNames[0] : 'currents'; // eslint-disable-line max-len
  // Setup app.
  ReactDOM.render(React.createElement(PostListing, { rootName }), appElement);
}
